<template>
  <div id="app" class="app" :class="{ 'rtl': isRTL }">
    <div v-if="showTrialBanner" class="trial-banner" role="status">
      <span>{{ t('onboarding.trialBanner', { days: onboarding.status?.remainingTrialDays ?? 0 }) }}</span>
      <router-link to="/onboarding/plan">{{ t('onboarding.choosePlan') }} →</router-link>
    </div>
    <router-view v-slot="{ Component }">
      <transition name="page-transition" mode="out-in">
        <!-- Ensure transition always receives an element root by wrapping the dynamic component -->
        <div v-if="Component" :key="route.fullPath" class="page-transition-wrapper">
          <component :is="Component" @signIn="handleSignIn" />
        </div>
      </transition>
    </router-view>
    
    <!-- Système de notifications legacy (pour les erreurs globales) -->
    <notification v-if="getError" :message="getError" type="error" @close="clearError" />
    
    <!-- Modal global -->
    <app-modal 
      v-if="getModal" 
      :title="getModal.title" 
      :message="getModal.message" 
      :confirm-text="getModal.confirmText" 
      :cancel-text="getModal.cancelText" 
      :is-visible="!!getModal"
      @confirm="handleModalConfirm" 
      @cancel="handleModalCancel" 
    />
    
    <!-- Nouveau système de notifications -->
    <NotificationContainer position="top-right" :max-notifications="5" />
    
    <!-- PrimeVue global components -->
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/composables/useStore'
import { useUiStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import archiWebAuthService from './services/archiweb.auth.service'
import Notification from './components/notifications/Notification.vue'
import AppModal from './components/base/Modal.vue'
import NotificationContainer from './components/notifications/NotificationContainer.vue'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'

// Store and router
const store = useStore()
const uiStore = useUiStore()
const auth = useAuthStore()
const onboarding = useOnboardingStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

// Computed properties
const isAuthenticated = computed(() => auth.isAuthenticated)
const isFirmOwner = computed(() => auth.user?.type === 'FIRM_OWNER')
const showTrialBanner = computed(() => isAuthenticated.value && isFirmOwner.value && onboarding.isTrial && !route.path.startsWith('/onboarding'))
const getError = computed(() => store.getters.getError.value)
const getModal = computed(() => uiStore.getModal)

const isRTL = computed(() => {
  // Check if the current language is RTL (Arabic)
  return (window as any).i18n?.global?.locale?.value === 'ar'
})

const isDevelopment = computed(() => {
  return process.env.NODE_ENV === 'development'
})

// Methods
const handleSignIn = async (): Promise<void> => {
  try {
    await archiWebAuthService.login()
  } catch (error) {
    console.error('Login error:', error)
    store.commit('SET_ERROR', 'Failed to start login process')
  }
}

const clearError = (): void => {
  store.commit('SET_ERROR', null)
}

const handleModalConfirm = (): void => {
  // Execute the callback provided in the modal config
  if (getModal.value && typeof getModal.value.onConfirm === 'function') {
    getModal.value.onConfirm()
  }
  uiStore.setModal(null)
}

const handleModalCancel = (): void => {
  if (getModal.value && typeof getModal.value.onCancel === 'function') {
    getModal.value.onCancel()
  }
  uiStore.setModal(null)
}

// Lifecycle hooks
onMounted(() => {
  // Debug i18n configuration
  console.log('=== App.vue i18n Debug ===')
  console.log('$i18n exists:', !!t)
  console.log('Current locale:', (window as any).i18n?.global?.locale?.value)
  console.log('Available locales:', (window as any).i18n?.global?.availableLocales)
  console.log('Translation test - dashboard.welcome:', t('dashboard.welcome'))
  console.log('Translation test - common.loading:', t('common.loading'))
  console.log('Window.i18n exists:', !!(window as any).i18n)
  console.log('Window.i18n locale:', (window as any).i18n?.global?.locale?.value)

  // Check authentication status on app initialization
  // This is already being handled in main.js with store.dispatch('checkAuth')

  // If we're at the home page and already authenticated, redirect to user home
  if (isAuthenticated.value && route.path === '/') {
    router.push('/')
  }
})// Watchers
watch(isAuthenticated, async (newValue: boolean) => {
  if (newValue && isFirmOwner.value && !onboarding.status) {
    try {
      await onboarding.refresh()
    } catch (error) {
      console.warn('Unable to refresh onboarding status for trial banner', error)
    }
  }
  if (newValue && route.path === '/') {
    router.push('/')
  }
}, { immediate: true })

watch(showTrialBanner, (visible: boolean) => {
  document.documentElement.classList.toggle('trial-banner-visible', visible)
}, { immediate: true })

onBeforeUnmount(() => {
  document.documentElement.classList.remove('trial-banner-visible')
})
</script>

<style>
:root { --trial-banner-offset: 0px; }
:root.trial-banner-visible { --trial-banner-offset: 40px; }
.trial-banner { position: fixed; inset: 0 0 auto; z-index: 1100; box-sizing: border-box; height: var(--trial-banner-offset); display: flex; align-items: center; justify-content: center; gap: 18px; padding: 8px 16px; background: #fff3df; color: #713b08; border-bottom: 1px solid #f5c57a; box-shadow: 0 2px 8px rgba(113,59,8,.08); font-size: 13px; font-weight: 700; }
.trial-banner span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.trial-banner a { flex: 0 0 auto; color: #155e91; white-space: nowrap; }
@media (max-width: 600px) {
  :root.trial-banner-visible { --trial-banner-offset: 48px; }
  .trial-banner { justify-content: space-between; gap: 10px; padding-inline: 12px; font-size: 12px; }
}
/* Page transitions with construction-app design system */
.page-transition-enter-active,
.page-transition-leave-active {
  transition: all var(--transition-base) cubic-bezier(0.4, 0, 0.2, 1);
}

.page-transition-enter-from {
  opacity: 0;
  transform: translateY(var(--space-4));
}

.page-transition-leave-to {
  opacity: 0;
  transform: translateY(calc(-1 * var(--space-4)));
}

/* RTL support */
.rtl {
  direction: rtl;
  text-align: right;
}

.rtl .app {
  text-align: right;
}
</style>
